import jsPDF from "jspdf";
import { formatDatum } from "./storage";

let fontB64 = null;
let fontBoldB64 = null;

async function fetchB64(path) {
  const res = await fetch(path);
  const buf = await res.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = "";
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

async function setupFonts(doc) {
  if (!fontB64) fontB64 = await fetchB64("/fonts/DejaVuSans.ttf");
  if (!fontBoldB64) fontBoldB64 = await fetchB64("/fonts/DejaVuSans-Bold.ttf");
  doc.addFileToVFS("DejaVuSans.ttf", fontB64);
  doc.addFont("DejaVuSans.ttf", "DejaVu", "normal");
  doc.addFileToVFS("DejaVuSans-Bold.ttf", fontBoldB64);
  doc.addFont("DejaVuSans-Bold.ttf", "DejaVu", "bold");
}

const M = 16;
const W = 210 - 2 * M;

function makeWriter(doc) {
  let y = M;
  const ensure = (h) => {
    if (y + h > 282) {
      doc.addPage();
      y = M;
    }
  };
  return {
    get y() { return y; },
    set y(v) { y = v; },
    ensure,
    title(text, size = 15) {
      ensure(12);
      doc.setFont("DejaVu", "bold");
      doc.setFontSize(size);
      doc.setTextColor(15, 23, 42);
      doc.text(text, 105, y, { align: "center" });
      y += size * 0.55;
    },
    section(text) {
      ensure(14);
      y += 4;
      doc.setFillColor(224, 242, 254);
      doc.rect(M, y - 4.5, W, 7, "F");
      doc.setFont("DejaVu", "bold");
      doc.setFontSize(10);
      doc.setTextColor(3, 105, 161);
      doc.text(text.toUpperCase(), M + 2, y);
      y += 7;
    },
    field(label, value) {
      const val = (value || "").trim() || "Nema unosa";
      doc.setFontSize(9);
      doc.setFont("DejaVu", "bold");
      doc.setTextColor(71, 85, 105);
      ensure(6);
      doc.text(label + ":", M, y);
      doc.setFont("DejaVu", "normal");
      doc.setTextColor(15, 23, 42);
      const lines = doc.splitTextToSize(val, W - 55);
      lines.forEach((line, i) => {
        ensure(5);
        doc.text(line, M + 52, y);
        y += 4.5;
        if (i < lines.length - 1) ensure(5);
      });
      y += 1;
    },
    block(label, value) {
      doc.setFontSize(9);
      doc.setFont("DejaVu", "bold");
      doc.setTextColor(71, 85, 105);
      ensure(10);
      doc.text(label + ":", M, y);
      y += 4.5;
      doc.setFont("DejaVu", "normal");
      doc.setTextColor(15, 23, 42);
      const val = (value || "").trim() || "Nema unosa";
      const lines = doc.splitTextToSize(val, W);
      lines.forEach((line) => {
        ensure(5);
        doc.text(line, M, y);
        y += 4.5;
      });
      y += 2;
    },
  };
}

export async function generatePdf(record, settings) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  await setupFonts(doc);
  const w = makeWriter(doc);

  const skola = settings.nazivSkole || "Naziv škole nije postavljen";
  w.y = M + 4;
  w.title(skola, 15);
  w.y += 2;
  w.title("DNEVNA EVIDENCIJA DEŽURANJA", 12);
  doc.setFont("DejaVu", "normal");
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  const tipLabel = record.tip === "nastavni" ? "Nastavni dan" : "Nenastavni dan";
  doc.text(`${tipLabel}  |  Datum: ${formatDatum(record.datum)}`, 105, w.y + 2, { align: "center" });
  w.y += 8;
  doc.setDrawColor(2, 132, 199);
  doc.setLineWidth(0.6);
  doc.line(M, w.y, 210 - M, w.y);
  w.y += 4;

  w.section("Osnovni podaci");
  w.field("Datum", formatDatum(record.datum));
  if (settings.opcije.smjene && record.osnovni.smjena) w.field("Smjena", record.osnovni.smjena);
  if (settings.opcije.podrucneSkole && record.osnovni.podrucnaSkola) w.field("Lokacija / područna škola", record.osnovni.podrucnaSkola);
  if (record.tip === "nenastavni") {
    w.field("Razlog nenastavnog dana", record.osnovni.razlogNenastavnog);
    w.block("Dežurno osoblje", record.osnovni.dezurnoOsoblje);
    w.field("Glavni dežurni djelatnik", record.osnovni.glavniDezurni);
  } else {
    w.block("Dežurno osoblje", record.osnovni.dezurnoOsoblje);
    w.field("Glavni dežurni učitelj", record.osnovni.glavniDezurni);
    w.block("Dežurni učitelji u prizemlju", record.osnovni.dezurniPrizemlje);
    w.block("Dežurni učitelji na katu", record.osnovni.dezurniKat);
    w.block("Dežurni učitelji u međusmjeni", record.osnovni.dezurniMedjusmjena);
    if (settings.opcije.dezurniUcenici && record.osnovni.dezurniUcenici) w.block("Dežurni učenici", record.osnovni.dezurniUcenici);
  }
  if (record.osnovni.napomena) w.block("Napomena", record.osnovni.napomena);

  if (record.tip === "nastavni" && settings.opcije.autobusi) {
    w.section("Autobusi");
    w.block("Dolasci autobusa", record.autobusi.dolasci);
    w.block("Odlasci autobusa", record.autobusi.odlasci);
  }

  w.section("Evidencija posjetitelja");
  if (record.posjetitelji.length === 0) {
    w.block("Posjetitelji", "Nije bilo posjetitelja");
  } else {
    record.posjetitelji.forEach((p, i) => {
      const parts = [
        p.ime && `Ime i prezime: ${p.ime}`,
        p.dolazak && `Dolazak: ${p.dolazak}`,
        p.odlazak && `Odlazak: ${p.odlazak}`,
        p.isprava && `ID isprava: ${p.isprava}`,
        p.razlog && `Razlog: ${p.razlog}`,
        p.osoba && `Posjećuje: ${p.osoba}`,
      ].filter(Boolean).join("  |  ");
      w.block(`Posjetitelj ${i + 1}`, parts || "Bez podataka");
    });
  }

  w.section("Zapažanja tijekom dežuranja");
  w.block("Evidencija štete u školi i oko škole", record.zapazanja.steta);
  w.block("Evidencija nasilnog ili neprimjerenog ponašanja", record.zapazanja.nasilje);
  w.block("Evidencija otuđenja stvari u školi", record.zapazanja.otudjenje);
  w.block("Evidencija nepoznatih osoba u školi", record.zapazanja.nepoznateOsobe);

  w.section("Posebni sigurnosni događaji");
  if (record.dogadjaji.length === 0) {
    w.block("Događaji", "Nije bilo posebnih sigurnosnih događaja");
  } else {
    record.dogadjaji.forEach((d, i) => {
      w.block(
        `Događaj ${i + 1}${d.datum ? ` (${formatDatum(d.datum)})` : ""}`,
        [d.vrsta && `Vrsta: ${d.vrsta}`, d.opis && `Opis: ${d.opis}`, d.mjere && `Poduzete mjere: ${d.mjere}`]
          .filter(Boolean)
          .join("\n") || "Bez podataka"
      );
    });
  }

  w.section("Pregled i zaključavanje");
  w.field("Evidenciju zaključao/la", record.pregled.zakljucao);
  w.field("Status", record.pregled.zakljucano ? "Evidencija je zaključana" : "Evidencija nije zaključana");
  w.field("Ravnatelj pregledao dana", record.pregled.ravnateljPregledao);
  w.block("Zapažanja ravnatelja", record.pregled.zapazanjaRavnatelja);

  w.ensure(26);
  w.y += 10;
  doc.setFont("DejaVu", "normal");
  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  doc.text("Dežurni učitelj (potpis): ______________________", M, w.y);
  doc.text("Ravnatelj (potpis): ______________________", 115, w.y);
  w.y += 12;
  doc.setFontSize(7.5);
  doc.setTextColor(148, 163, 184);
  doc.text(`Izvješće generirano ${new Date().toLocaleString("hr-HR")} putem aplikacije za evidenciju dežuranja.`, 105, w.y, { align: "center" });

  doc.save(`evidencija-dezuranja-${record.datum}-${record.tip}.pdf`);
}
